import { Dialog, DialogBackdrop, Menu, Transition, TransitionChild } from '@headlessui/react';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { usePermission } from '../../../../hooks/usePermission';
import { accountSelector, authSelector, useAppDispatch } from '../../../../store';
import { SignOutUser } from '../../../../store/Auth';
import { getCurrentYear, getInitials } from '../../../../utils';
import { navItemObjects, sageAdminNavItemObjects } from '../../NavConstant';
import SidebarNavHeader from '../../SibarNavHeader';
import NavItem from '../NavItem';

export interface SmallScreenNavProps {
  sidebarOpen: boolean;
  closeSideBar: () => void;
}

export default function SmallScreenNav({ sidebarOpen, closeSideBar }: SmallScreenNavProps) {
  const { data: currentUserData } = useSelector(authSelector);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: accountInfo } = useSelector(accountSelector);
  const navItems = currentUserData.account === 'sageadmin' ? sageAdminNavItemObjects : navItemObjects;
  const { hasPermission } = usePermission();
  const signoutHandller = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      dispatch(SignOutUser());
      navigate('/auth/sign_in', { replace: true });
    } catch (_err) {}
  };

  return (
    <Transition show={sidebarOpen} as={Fragment}>
      <Dialog as='div' className='fixed inset-0 flex z-40 lg:hidden' onClose={closeSideBar}>
        <TransitionChild
          as={Fragment}
          enter='transition-opacity ease-linear duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='transition-opacity ease-linear duration-300'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <DialogBackdrop className='fixed inset-0 bg-gray-600/75' />
        </TransitionChild>
        <TransitionChild
          as={Fragment}
          enter='transition ease-in-out duration-300 transform'
          enterFrom='-translate-x-full'
          enterTo='translate-x-0'
          leave='transition ease-in-out duration-300 transform'
          leaveFrom='translate-x-0'
          leaveTo='-translate-x-full'
        >
          <div className='relative flex-1 flex flex-col max-w-xs w-full bg-white focus:outline-hidden text-neutralColor-normal'>
            <TransitionChild
              as={Fragment}
              enter='ease-in-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in-out duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='absolute top-0 right-0 -mr-12 pt-2'>
                <button
                  type='button'
                  className='ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white'
                  onClick={closeSideBar}
                >
                  <span className='sr-only'>Close sidebar</span>
                  <XCircleIcon className='h-6 w-6 text-white' aria-hidden='true' />
                </button>
              </div>
            </TransitionChild>
            <div className='flex-1 h-0 pt-5 pb-4 overflow-y-auto'>
              <SidebarNavHeader />
              <nav aria-label='Sidebar' className='mt-5'>
                <div className='px-2 space-y-1'>
                  {navItems.map((item) => {
                    const { name, permissions, subscription } = item;
                    if (hasPermission(permissions)) {
                      if (subscription && !accountInfo?.subscriptions.includes(subscription)) {
                        return null;
                      }
                      return <NavItem key={name} item={item} isFullNavHidden={false} onItemClick={closeSideBar} />;
                    }
                    return null;
                  })}
                </div>
              </nav>
            </div>

            <div>
              <Menu as='div' className='relative w-full'>
                <div className='flex flex-col gap-4'>
                  <div
                    className='bg-white rounded-full flex text-sm items-center gap-4 px-4 cursor-pointer hover:bg-gray-50'
                    onClick={() => navigate('/app/settings/profile')}
                  >
                    <span className='sr-only'>Open user menu</span>
                    {currentUserData.profileImageUrl && (
                      <img className='h-14 w-14 rounded-full' src={currentUserData.profileImageUrl} alt='Profile' />
                    )}
                    {!currentUserData.profileImageUrl && (
                      <div className='h-10 w-10 bg-gray-200 rounded-full flex justify-center items-center text-md p-2 text-primaryColor-normal font-bold tracking-wider'>
                        {getInitials(currentUserData.name)}
                      </div>
                    )}
                    <div className='text-left'>
                      <h3 className='text-sm font-bold'>{currentUserData.name ? currentUserData.name : 'unknown user'}</h3>
                      <span className='text-neutralColor-normal text-xs'>{currentUserData.title}</span>
                    </div>
                  </div>
                </div>
              </Menu>
              <div
                className='flex p-5 items-center space-x-2 cursor-pointer text-neutralColor-normal hover:bg-primaryColor-lightest hover:text-neutralColor-medium'
                onClick={signoutHandller}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='h-6 w-6'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9'
                  />
                </svg>
                <span>Sign Out</span>
              </div>
            </div>

            <div className='shrink-0 flex border-t border-gray-200 p-4'>
              <div className='flex flex-col gap-4'>
                <div className='flex items-center shrink-0 px-4'>
                  <img
                    className='h-6 w-auto'
                    src='https://assets.seinterminal.com/images/logos/SEIN_Terminal_Logo_Color.png'
                    alt='sein-terminal-logo'
                  />
                </div>
                <div className='ml-3'>
                  <p className='text-xs text-gray-700 group-hover:text-neutralColor-dark'>
                    &#169; {getCurrentYear()} SEIN Technology Partners.
                  </p>
                  <p className='text-xs text-gray-500 group-hover:text-neutralColor-normal'>All Rights Reserved</p>
                </div>
              </div>
            </div>
          </div>
        </TransitionChild>
        <div className='shrink-0 w-14' aria-hidden='true'>
          {/* Force sidebar to shrink to fit close icon */}
        </div>
      </Dialog>
    </Transition>
  );
}
